import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import FetchServer from 'utils/fetch/FetchServer'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import selectEmployeeLanguage from 'services/redux/data/selectors/selectEmployeeLanguage'
import dayjs from 'dayjs'
import { useConfigProvider } from 'components/provider/ConfigProvider'
import { useSplash } from 'components/provider/SplashProvider'
import HelperDate from 'utils/helper-date'
import { setData } from 'services/redux/data/actions'
import { dataConstants } from 'services/redux/data/constants'
import { AppState } from 'react-native'
import { authenticationConstants } from 'services/redux/authentication/constants'
import SecureStoreService from 'services/store/secure-store'
import { storeKeys } from 'services/store/keys'
import { cartConstants } from 'services/redux/cart/constants'
import { SET_CART } from 'services/redux/action-types'

export const ServerProviderContext = createContext( null );

export const useServer  = () => {
	return useContext( ServerProviderContext );
}

const ServerProvider = ({ children }) => {
	
	const dispatch = useDispatch()
	
	const [ loading, setLoading ] = useState( true )
	const [ server, setServer ] = useState({})
	const [ timezone, setTimezone ] = useState( dayjs.tz.guess() )
	
	const appState = useRef( AppState.currentState );
	const [ appStateVisible, setAppStateVisible ] = useState( appState.current );
	const [ appTimezone, setAppTimezone ] = useState( dayjs.tz.guess() )
	
	const { customerId } = useConfigProvider()
	const { setLoadingError } = useSplash()
	
	const _loggedIn = useSelector( state => {
		return state.authentication[authenticationConstants.LOGGED_IN];
	} )
	const _employeeLanguage = useSelector( selectEmployeeLanguage )
	const employeeLanguage = _.get( _employeeLanguage, ['code'], null );
	
	const fetchServer = () => {
		// With the check :)
		SecureStoreService
			.get( storeKeys.EMPLOYEE_GUID )
			.then( employeeGuid => {
				// No customer ID yet or no employee guid found? Then we are still logging in and we do not have to fetch the server settings
					if( _.isEmpty( customerId ) || _.isEmpty( employeeGuid ) ) {
						setLoading( false )
						return;
					}

					// Server time improves accuracy, but should not block cached UI.
					setLoading( false )

					FetchServer.fetchAll({
					callbackSuccess: ( data ) => {
						setServer( data )
						
						// Set the defaults
						dayjs.tz.setDefault( data.server_timezone )
						setTimezone( data.server_timezone )
						
						// Also set in Redux
						dispatch( setData( dataConstants.SERVER_TIME, data.server_time ) );
						dispatch( setData( dataConstants.SERVER_TIMEZONE, data.server_timezone ) );
						dispatch( setData( dataConstants.SERVER_TIMEZONE_OFFSET, parseInt( data.server_utc_offset ) ) );
						dispatch( setData( dataConstants.SERVER_TIME_DIFFERENCE_SECONDS, HelperDate.compareDeviceWithServer() ) );
						dispatch( setData( cartConstants.BOOK_DATE, HelperDate.date() ) );
						
						// Init the start date
						dispatch({
							type: SET_CART,
							key: cartConstants.BOOK_DATE,
							payload: HelperDate.date()
						});
						
						},
					callbackError: ( error ) => {
						if( error?.error === 'no_base_url' ) {
							setLoading( false )
							return
						}
						
							setLoadingError( error )
					}
				});
			})
	}
	
	useEffect( () => {
		// Guess the timezone
		dayjs.tz.setDefault( dayjs.tz.guess() )
		
		for( const localeName of [ 'en', 'nl' ] ) {
			dayjs.updateLocale( localeName, {
				weekStart: 1
			})
		}
		
		// Get the settings
		fetchServer();
	}, [ customerId, _loggedIn ] )
	
	useEffect( () => {
		if( appStateVisible ) {
			const newAppTimezone = dayjs.tz.guess()
			
			if( appTimezone !== newAppTimezone ) {
				setAppTimezone( newAppTimezone )
				
				// Fetch server again
				fetchServer()
			}
		}
	}, [ appStateVisible ] )
	
	useEffect(() => {
		const subscription = AppState.addEventListener('change', nextAppState => {
			appState.current = nextAppState;
			setAppStateVisible(appState.current);
		});
		
		return () => {
			subscription.remove();
		};
	}, []);
	
	useEffect( () => {
		switch( _.toLower( employeeLanguage ) ) {
			case 'nl-nl':
				dayjs.locale('nl')
				break;
				
			default:
				dayjs.locale('en')
		}
	}, [ employeeLanguage ] );
	
	return (
		<ServerProviderContext.Provider
			value={{
				server: server,
				timezone: timezone,
			}}>
			
			{ loading ? null : children }
		</ServerProviderContext.Provider>
	)
}

export default ServerProvider
